import EnvConfig from "../config/env.config";
import { ClientStorage, ClientStorageItem } from "./clientstorage";
import { handleUnauthorizedResponse } from "./handle.unauthorized";
// import Cookies from 'js-cookie'

const prepareArray = (arr: any[]): any[] => {
  return arr.map(prepareVal).filter((v) => v !== null);
};

const prepareVal = (val: any) => {
  if (!val) {
    if (val === 0 || val === false) return val;
  } else {
    if (typeof val === "object") {
      if (Array.isArray(val)) {
        if (Array.isArray(val)) return prepareArray(val);
      } else return prepareObject(val);
    } else return val;
  }
  return null;
};

const prepareObject = (obj: { [key: string]: any }) => {
  const res: { [key: string]: any } = {};
  for (const [key, val] of Object.entries(obj)) {
    const valRes = prepareVal(val);
    if (valRes !== null) res[key] = valRes;
  }
  return res;
};

const convertObject = (obj: { [key: string]: any }) => {
  let res = "";
  if (Object.keys(obj).length === 0) return res;
  for (const [key, val] of Object.entries(obj))
    res += `${res ? "&" : ""}${key}=${val}`;
  return res;
};

export const __internallyPrepareRequest = ({
  baseRoute,
  ext,
  params,
  query,
}: Partial<BASE_API_REQ>) =>
  (baseRoute || EnvConfig.apiBaseUrl) +
  (ext ? `/${ext}` : "") +
  (params ? `/${params}` : "") +
  (query ? `?${convertObject(prepareObject(query))}` : "");

export type BASE_API_REQ = {
  baseRoute: string; // doesn't ends with /
  ext: string;
  params: string;
  query: Record<string, string | number | undefined>;
  body: FormData | string | Record<string, any>;
  token: string;
  headers: Record<string, string>;
};

export type ResponseType<T> = {
  statusCode: number;
  message: string;
  payload: T;
  error: string | null;
  token: string | null;
};

export type responseCallback<T> = (response: ResponseType<T>) => void;

export const KCacheTimeOut = 1000 * 60 * 5; // 5 minutes
const GETCacheTable: Record<
  string,
  { requestID: string; data: any; timestamp: number }
> = {};
const RequestRing: Array<{ currRequest: string; timestamp: number }> = [];
export const KRequestRingLength = 30;
export const KRequestRingDuration = 200; // 200ms

const mergeCurrentRequestToRing = (currentRequest: string): boolean => {
  if (RequestRing.length >= KRequestRingLength) {
    RequestRing.shift(); // Remove oldest request
  }

  const now = Date.now();
  const prevRequest = RequestRing.find(
    ({ currRequest }) => currRequest === currentRequest,
  );
  if (prevRequest) {
    const diff = now - prevRequest.timestamp;
    if (diff >= KRequestRingDuration) {
      RequestRing.push({ currRequest: currentRequest, timestamp: now });
      return true;
    }
    return false;
  } else {
    RequestRing.push({ currRequest: currentRequest, timestamp: now });
    return true;
  }
};

const GetRequestsSubscrptionMap: Record<
  string, // reqUrl
  ((value: ResponseType<any> | PromiseLike<ResponseType<any>>) => void)[]
> = {};

const subscribeAndWait = async <T>(
  reqUrl: string,
): Promise<ResponseType<T>> => {
  return new Promise((resolve) => {
    const entry = GetRequestsSubscrptionMap[reqUrl];
    if (!entry) {
      GetRequestsSubscrptionMap[reqUrl] = [resolve];
    } else {
      entry.push(resolve);
    }
  });
};

export const __internallyCacheRequestResult = <T>(
  reqUrl: string,
  data: ResponseType<T>,
) => {
  const requestID = Math.random().toString();
  GETCacheTable[reqUrl] = {
    requestID,
    data,
    timestamp: Date.now(),
  };
};

const REQ_METHOD = async <T>(
  reqMethod: "POST" | "GET" | "PUT" | "DELETE",
  baseApiReq: Partial<BASE_API_REQ>,
  useCache = true, // Optionally bypass cache
) => {
  const { body, headers } = baseApiReq;
  const reqUrl = __internallyPrepareRequest(baseApiReq);
  const bodyIsOriginallyStringOrFormData =
    typeof body === "string" || body instanceof FormData;
  const reqBody = body
    ? bodyIsOriginallyStringOrFormData
      ? body
      : JSON.stringify(prepareObject(body!))
    : "";

  const accessToken = ClientStorage.fetch(ClientStorageItem.AccessToken);
  const XAccountHash = ClientStorage.fetch(ClientStorageItem.XAccountHash);
  const weddingVersion = ClientStorage.fetch(ClientStorageItem.WeddingVersion);
  const sentHeaders = {
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    ...(XAccountHash ? { "X-Account-Hash": XAccountHash } : {}),
    ...(reqMethod !== "GET" && weddingVersion
      ? { "If-Match": weddingVersion }
      : {}),
    ...(!bodyIsOriginallyStringOrFormData
      ? { "Content-Type": "application/json" }
      : {}),
    ...headers,
  };
  const payload = {
    method: reqMethod,
    credentials: "include" as RequestCredentials,
    headers: {
      ...sentHeaders,
    },
    ...(body ? { body: reqBody } : {}),
  };

  // Cache Handling for GET
  if (reqMethod === "GET" && useCache) {
    const dataInCache = GETCacheTable[reqUrl];
    if (dataInCache) {
      const timelapse = Date.now() - dataInCache.timestamp;
      if (timelapse <= KCacheTimeOut) {
        return dataInCache.data as ResponseType<T>;
      }
    }
  }

  // Deduplication Using Request Ring
  const currentRequest =
    reqMethod + reqUrl + (typeof reqBody === "string" ? reqBody : "");
  const makeRequest = mergeCurrentRequestToRing(currentRequest);
  // console.log({ makeRequest, useCache, reqUrl });
  if (!makeRequest) {
    if (reqMethod === "GET") {
      // Get requests shouldn't mutate state.
      // This means we can just delay until there is a
      // result from the previous queries we sent and then
      // return the result to the same "GET" listener.
      const eventualResult = await subscribeAndWait<T>(reqUrl);
      return eventualResult;
    } else {
      return {
        statusCode: 400,
        message: "Duplicate request within time frame",
        payload: "" as T,
        error: "DuplicateRequest",
        token: "",
      };
    }
  }

  try {
    const res = await fetch(reqUrl, payload);

    // Safe JSON parsing to handle empty responses (204, etc.)
    let data: ResponseType<T>;
    try {
      const text = await res.text();
      if (text) {
        data = JSON.parse(text) as ResponseType<T>;
        // Some legacy endpoints omit statusCode from otherwise valid responses.
        // Use the actual HTTP status so successful requests are not reported as errors.
        data.statusCode = data.statusCode || res.status;
      } else {
        // Empty response - create success response
        data = {
          statusCode: res.status,
          message: "Success",
          payload: {} as T,
          error: null,
          token: null,
        };
      }
    } catch (jsonError) {
      // Malformed JSON - treat as error
      throw new Error("Invalid response format");
    }

    handleUnauthorizedResponse(res.status, data.message);

    // Keep optimistic-concurrency state in sync after every legacy response.
    // This previously lived below an unconditional return in the duplicate
    // request branch, so successful wedding mutations could leave subsequent
    // saves (including table assignments) with a stale If-Match value.
    const responseVersion = (data.payload as any)?.version;
    const currentVersion = (data.payload as any)?.currentVersion;
    const nextWeddingVersion =
      typeof responseVersion === "number"
        ? responseVersion
        : typeof currentVersion === "number"
          ? currentVersion
          : null;
    if (nextWeddingVersion !== null) {
      ClientStorage.store(
        ClientStorageItem.WeddingVersion,
        String(nextWeddingVersion),
      );
    }

    if (reqMethod === "GET") {
      // if it's a "GET" request, push the result to an awaitable promise
      // that other listeners can wait on.
      try {
        const waitQueue = GetRequestsSubscrptionMap[reqUrl];
        if (waitQueue?.length) {
          for (const resolve of waitQueue) {
            resolve(data);
          }
        }
        delete GetRequestsSubscrptionMap[reqUrl];
      } catch (localErr) {
        // don't stop because of others
        console.error(localErr);
      }
    }

    // Store in Cache for GET Requests
    if (
      reqMethod === "GET" &&
      useCache &&
      !data.error &&
      data.statusCode < 400
    ) {
      __internallyCacheRequestResult<T>(reqUrl, data);
    }

    return data;
  } catch (error: any) {
    const message = String(error?.message || "");
    const isNetworkFailure =
      error instanceof TypeError ||
      /load failed|failed to fetch|network request failed/i.test(message);
    return {
      statusCode: 500,
      message: "Network error",
      payload: "" as T,
      error: isNetworkFailure
        ? "We couldn't reach WEDDN. Check your connection and try again."
        : message || "Unknown error",
      token: "",
    };
  }
};

export const POST = <T>(baseApiReq: Partial<BASE_API_REQ>, useCache = true) =>
  REQ_METHOD<T>("POST", baseApiReq, useCache);
export const PUT = <T>(baseApiReq: Partial<BASE_API_REQ>, useCache = true) =>
  REQ_METHOD<T>("PUT", baseApiReq, useCache);
export const DELETE = <T>(baseApiReq: Partial<BASE_API_REQ>, useCache = true) =>
  REQ_METHOD<T>("DELETE", baseApiReq, useCache);
export const GET = <T>(baseApiReq: Partial<BASE_API_REQ>, useCache = true) =>
  REQ_METHOD<T>("GET", baseApiReq, useCache);
